// Motores en 3D: motor de CD 130 con hélice o disco, 28BYJ-48 con su placa ULN2003 y NEMA 17 con A4988.
import * as THREE from 'three';
import { Builder, M, mat, text } from '../kit.js';
import { DCMOTOR, BYJ, NEMA, GEARMOTOR } from '../../parts/actuators.js';
import {
  P, SILK, pcb, holeRings, silk, pad, traces, maleHeader, jumperCap, screwTerminal, led3mm, labelPlane, dipOnBoard,
  electrolytic, smd, cable, tinnedTip, sharedGeo, sharedTexture,
} from './actuators-common.js';

const TAU = Math.PI * 2;

/** Círculo con dos caras planas (|y| ≤ f), como la lata de un motor 130 o un eje de doble plano. */
function flatCircle(r, f) {
  const a = Math.asin(Math.min(1, f / r));
  const s = new THREE.Shape();
  s.moveTo(r * Math.cos(-a), -f);
  s.absarc(0, 0, r, -a, a, false);
  s.lineTo(-r * Math.cos(a), f);
  s.absarc(0, 0, r, Math.PI - a, Math.PI + a, false);
  s.lineTo(r * Math.cos(-a), -f);
  return s;
}

/** Prisma vertical a partir de una forma en planta (forma en x, −z), con base en y0. */
function prism(shape, h, curveSegments = 24) {
  const g = new THREE.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false, curveSegments });
  g.rotateX(-Math.PI / 2);
  return g;
}

// Motor de CD 130 ----------------------------------------------------------------------------------
function propeller(group) {
  const red = mat('#d8352a', { roughness: 0.45 });
  const hub = new THREE.Mesh(sharedGeo('prop-hub', () => new THREE.CylinderGeometry(3.3, 3.6, 5.2, 20)), red);
  group.add(hub);
  const blade = sharedGeo('prop-blade', () => {
    const s = new THREE.Shape();
    s.moveTo(2.2, -3.8);
    s.lineTo(22, -3.0);
    s.quadraticCurveTo(27.5, -2.6, 27, 0.6);
    s.quadraticCurveTo(26.2, 3.0, 21, 2.9);
    s.lineTo(2.2, 3.4);
    s.closePath();
    const g = new THREE.ExtrudeGeometry(s, { depth: 1.0, bevelEnabled: false, curveSegments: 8 });
    g.translate(0, 0, -0.5);
    g.rotateX(-Math.PI / 2);
    return g;
  });
  for (const k of [0, 1]) {
    const m = new THREE.Mesh(blade, red);
    m.rotation.set(0, k * Math.PI, 0, 'YXZ');
    m.rotateX(0.38); // paso de la hélice
    m.castShadow = true;
    group.add(m);
  }
  // Punta blanca en una pala para ver el giro
  const tip = new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.15, 5.4), mat('#f4f4ef', { roughness: 0.5 }));
  tip.position.set(24.4, 0, 0);
  tip.rotation.x = 0.38;
  group.add(tip);
  return 28;
}

function disc(group) {
  const tex = sharedTexture('motor-disc', 36, 36, (g) => {
    g.fillStyle = '#f3f2ec';
    g.fillRect(0, 0, 36, 36);
    for (let i = 0; i < 4; i++) {
      g.fillStyle = i % 2 ? '#f3f2ec' : '#1d1e22';
      g.beginPath();
      g.moveTo(18, 18);
      g.arc(18, 18, 18, (i * Math.PI) / 2, ((i + 1) * Math.PI) / 2);
      g.fill();
    }
    g.fillStyle = '#d8352a';
    g.beginPath();
    g.arc(18, 18, 15.5, -0.25, 0.25);
    g.arc(18, 18, 11, 0.25, -0.25, true);
    g.fill();
    g.fillStyle = '#9aa0a6';
    g.beginPath();
    g.arc(18, 18, 3.2, 0, TAU);
    g.fill();
  }, 12);
  const top = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.5 });
  const side = mat('#e7e5dd', { roughness: 0.5 });
  const m = new THREE.Mesh(sharedGeo('motor-disc', () => new THREE.CylinderGeometry(18, 18, 1.4, 48)), [side, top, side]);
  m.castShadow = true;
  group.add(m);
  return 18;
}

export function buildDcMotor(def, props) {
  const D = DCMOTOR;
  const b = new Builder();
  const plastic = mat('#2a2b30', { roughness: 0.62 });
  // Soporte: base y manga que sujeta la lata
  b.rbox(26, D.BASE_H, 26, 1.6, plastic, 0, 0, 0);
  b.cyl(D.SLEEVE_R, D.SLEEVE_R + 0.5, D.SLEEVE_TOP - D.BASE_H, plastic, 0, D.BASE_H, 0, 40);
  b.box(2.4, 3.6, 5.8, M.hole, -D.SLEEVE_R + 0.2, D.BASE_H, 0);
  for (const s of [-1, 1]) b.cyl(1.4, 1.4, 0.3, M.hole, s * 9.5, D.BASE_H, s * 9.5, 12);
  // Lata con caras planas, tapa frontal, buje y eje
  const can = mat('#c3c7cc', { roughness: 0.3, metalness: 0.9 });
  b.add(sharedGeo('m130-can', () => prism(flatCircle(D.R, D.FLAT), D.CAN_TOP - D.SLEEVE_TOP + 0.5, 32)), can, { y: D.SLEEVE_TOP - 0.5 });
  b.cyl(7.2, 7.4, 0.7, can, 0, D.CAN_TOP, 0, 32);
  for (const s of [-1, 1]) b.cyl(1.1, 1.1, 0.05, M.hole, s * 4.6, D.CAN_TOP + 0.7, 0, 12);
  b.cyl(3.0, 3.0, 1.8, M.gold, 0, D.CAN_TOP + 0.7, 0, 20);
  b.cyl(1.0, 1.0, D.SHAFT_TOP - D.CAN_TOP - 2.5, M.steel, 0, D.CAN_TOP + 2.5, 0, 12);
  // Cables rojo (+) y negro (−) que salen por la ranura del soporte
  for (const [z, color] of [[-P / 2, '#d23a2e'], [P / 2, '#1e1f22']]) {
    cable(b, [[-9, 5.5, z * 0.8], [-12.8, 4.2, z * 0.9], [-16, 1.6, z], [-22, 1.0, z], [D.LEAD_X + 3.2, 1.0, z]], 0.55, color, 20);
    tinnedTip(b, D.LEAD_X, 1.0, z, -1, 3.2);
  }
  b.build();

  // Rotor (gira): hélice o disco y el difuminado cuando va rápido
  const rotor = new THREE.Group();
  rotor.position.y = D.ROTOR_Y;
  const radius = props.rotor === 'disc' ? disc(rotor) : propeller(rotor);
  b.root.add(rotor);
  const blur = new THREE.Mesh(
    sharedGeo(`blur${radius}`, () => new THREE.CircleGeometry(radius, 48).rotateX(-Math.PI / 2)),
    new THREE.MeshBasicMaterial({ color: props.rotor === 'disc' ? '#9a9a96' : '#d8352a', transparent: true, opacity: 0, depthWrite: false }),
  );
  blur.position.y = D.ROTOR_Y + 1.6;
  blur.visible = false;
  blur.userData.noPick = true;
  blur.renderOrder = 3;
  b.root.add(blur);

  // Freno: una abrazadera bajo el rotor con su palanca, que sale por delante más allá del disco o la hélice; gris
  // suelta, se pone roja y baja un poco al apretar
  const brakeMat = new THREE.MeshStandardMaterial({ color: '#8a8d93', roughness: 0.5, metalness: 0.5 });
  const brake = new THREE.Group();
  brake.position.y = 35.2;
  const collar = new THREE.Mesh(sharedGeo('brake-collar', () => new THREE.CylinderGeometry(2.3, 2.3, 1.9, 20)), brakeMat);
  const lever = new THREE.Mesh(sharedGeo('brake-lever', () => new THREE.BoxGeometry(2.6, 1.3, 23)), brakeMat);
  lever.position.z = 12.5;
  const knob = new THREE.Mesh(sharedGeo('brake-knob', () => new THREE.CylinderGeometry(2.1, 2.1, 5.2, 16)), brakeMat);
  knob.position.set(0, -0.4, 24.2);
  for (const m of [collar, lever, knob]) {
    m.castShadow = true;
    brake.add(m);
  }
  b.root.add(brake);
  const idle = new THREE.Color('#8a8d93');
  const tight = new THREE.Color('#d6322a');
  const setBrake = (f) => {
    const g = Math.min(1, Math.max(0, Number(f) || 0));
    brakeMat.color.copy(idle).lerp(tight, g);
    brake.rotation.x = 0.05 * g;
  };
  setBrake(props.brake);

  let rpm = 0;
  let shown = 0;
  let angle = 0;
  return {
    root: b.root,
    update(v) {
      rpm = v.rpm || 0;
    },
    setProps(p) {
      setBrake(p.brake);
    },
    animate(dt) {
      shown += (rpm - shown) * Math.min(1, dt * 10);
      if (Math.abs(shown) < 1 && rpm === 0) shown = 0;
      // Giro visible (mucho más lento que el real): 2500 rpm → 1 vuelta por segundo
      const rps = Math.max(-6, Math.min(6, shown / 2500));
      angle -= rps * TAU * dt; // positivo = horario visto desde arriba
      rotor.rotation.y = angle % TAU;
      blur.material.opacity = Math.min(0.4, Math.max(0, (Math.abs(shown) - 2500) / 12000));
      blur.visible = blur.material.opacity > 0.01;
      return shown !== 0;
    },
    reset() {
      rpm = 0;
    },
  };
}

/** Zapata de freno (caja w × h × d): gris suelta y roja cuanto más aprieta; set(f, mover) recibe la fracción 0–1 de freno. */
function brakeShoe(w = 2.4, h = 1.8, d = 3.6) {
  const material = new THREE.MeshStandardMaterial({ color: '#8a8d93', roughness: 0.6 });
  const mesh = new THREE.Mesh(sharedGeo(`brake-shoe${w},${h},${d}`, () => new THREE.BoxGeometry(w, h, d)), material);
  mesh.castShadow = true;
  const idle = new THREE.Color('#8a8d93');
  const full = new THREE.Color('#d6322a');
  return {
    mesh,
    set(f, move) {
      const g = Math.min(1, Math.max(0, Number(f) || 0));
      material.color.copy(idle).lerp(full, g);
      move(g);
    },
  };
}

// 28BYJ-48 con placa ULN2003 ------------------------------------------------------------------------
const WIRE_COLORS = ['#2f6fe0', '#e86fa8', '#f0cf2a', '#f08a1c', '#d8322c']; // azul, rosa, amarillo, naranja, rojo

export function buildStepper28byj(def, props) {
  const { W, D, PCB_Y, TOP, IN_X, PWR_Z, PWR_X, MOTOR_X: MX, MOTOR_Z: MZ, R, H, SHAFT_OFF } = BYJ;
  const b = new Builder();
  const holes = [[-15, -13.5, 1.5], [15, -13.5, 1.5], [-15, 13.5, 1.5], [15, 13.5, 1.5]];
  const ledX = 10.2;
  const ledZ = [-6.3, -2.1, 2.1, 6.3];
  const jstX = 4;
  const jstZ = -13.0;
  pcb(b.root, {
    key: 'uln2003-pcb', w: W, d: D, y0: PCB_Y, color: '#1d6a39', edge: '#154f2a', holes,
    draw: (g, X, Y) => {
      traces(g, 'rgba(8,60,26,0.7)', [
        ...[0, 1, 2, 3].map((i) => [[X(IN_X), Y((i - 1.5) * P)], [X(-9), Y((i - 1.5) * P)], [X(-6.8), Y(-8 + i * 2.54)]]),
        ...[0, 1, 2, 3].map((i) => [[X(0.8), Y(-8 + i * 2.54)], [X(6), Y(ledZ[i])], [X(ledX), Y(ledZ[i])]]),
        ...[0, 1, 2, 3, 4].map((i) => [[X(jstX - 5 + i * 2.5), Y(jstZ)], [X(jstX - 5 + i * 2.5), Y(-9.5)], [X(0.8 + (i % 4)), Y(-6)]]),
      ], 0.5);
      for (let i = 0; i < 4; i++) {
        pad(g, X(IN_X), Y((i - 1.5) * P), 0.95, i === 0);
        silk(g, `IN${i + 1}`, X(IN_X + 2.0), Y((i - 1.5) * P), { size: 1.0, align: 'left' });
        silk(g, 'ABCD'[i], X(ledX + 3.6), Y(ledZ[i]), { size: 1.2 });
      }
      silk(g, '−', X(PWR_X), Y(PWR_Z - 2.4), { size: 1.6 });
      silk(g, '+', X(PWR_X + P), Y(PWR_Z - 2.4), { size: 1.6 });
      silk(g, '5-12V', X(PWR_X - 4.4), Y(PWR_Z), { size: 0.95 });
      silk(g, 'ON', X(10.67), Y(PWR_Z - 2.4), { size: 0.9 });
      silk(g, 'ULN2003', X(-3), Y(13.2), { size: 0.9 });
    },
  });
  holeRings(b, holes, TOP);
  maleHeader(b, [0, 1, 2, 3].map((i) => [IN_X, (i - 1.5) * P]), TOP);
  maleHeader(b, [[PWR_X, PWR_Z], [PWR_X + P, PWR_Z]], TOP);
  const pj = [[9.4, PWR_Z], [11.94, PWR_Z]];
  maleHeader(b, pj, TOP);
  jumperCap(b, pj[0], pj[1], TOP);
  dipOnBoard(b.root, b, 16, -3, TOP, 1.5, 'z', 'ULN2003APG', 'TOSHIBA');
  for (let i = 0; i < 4; i++) smd(b, 6.2, TOP, ledZ[i], 'r');
  electrolytic(b.root, -10.5, TOP, -10.6, 2.5, 5.5, { color: '#1d2a4a' });
  // Conector JST XH de 5 vías con la clavija del motor
  const white = mat('#eeeae1', { roughness: 0.55 });
  b.box(12.9, 7, 5.8, white, jstX, TOP, jstZ);
  b.box(11.8, 4.2, 4.4, mat('#f6f3eb', { roughness: 0.5 }), jstX, TOP + 7, jstZ);
  // Motor: lata, brida con orejas, tapa azul de los cables y buje del eje
  const steel = mat('#c4c8cd', { roughness: 0.32, metalness: 0.9 });
  b.cyl(R, R, H - 0.8, steel, MX, 0, MZ, 48);
  const flange = sharedGeo('byj-flange', () => {
    const s = new THREE.Shape();
    s.moveTo(-17.5, -3.6);
    s.lineTo(17.5, -3.6);
    s.absarc(17.5, 0, 3.6, -Math.PI / 2, Math.PI / 2, false);
    s.lineTo(-17.5, 3.6);
    s.absarc(-17.5, 0, 3.6, Math.PI / 2, (3 * Math.PI) / 2, false);
    for (const x of [-17.5, 17.5]) {
      const h = new THREE.Path();
      h.absarc(x, 0, 2.1, 0, TAU, true);
      s.holes.push(h);
    }
    return prism(s, 0.8, 20);
  });
  b.add(flange, steel, { x: MX, y: H - 0.8, z: MZ });
  b.cyl(R - 0.3, R - 0.3, 0.8, steel, MX, H - 0.8, MZ, 48);
  b.cyl(4.6, 4.8, 1.6, steel, MX, H, MZ - SHAFT_OFF, 24);
  b.rbox(17.5, 16.5, 4.6, 1.0, mat('#2c5fd9', { roughness: 0.45 }), MX, 1.2, MZ + R + 0.9);
  // Cables del motor a la clavija
  WIRE_COLORS.forEach((c, i) => {
    const xm = MX + (i - 2) * 1.6;
    const xj = jstX + (i - 2) * 2.5;
    cable(b, [[xm, 9, MZ + R + 3], [xm, 14, MZ + R + 6], [(xm + xj) / 2, 19.5, (MZ + R + jstZ) / 2 + 3], [xj, 17.5, jstZ - 0.6], [xj, TOP + 11, jstZ]], 0.5, c, 24);
  });
  b.build();

  // LEDs A–D
  const leds = ledZ.map((z) => led3mm(b.root, b, ledX, TOP, z, '#ff2a1c'));
  b.build();

  // Eje con indicador (gira)
  const shaft = new THREE.Group();
  shaft.position.set(MX, 0, MZ - SHAFT_OFF);
  const shaftGeo = sharedGeo('byj-shaft', () => prism(flatCircle(2.5, 1.5), 9.4, 16));
  const s = new THREE.Mesh(shaftGeo, M.steel);
  s.position.y = H + 1.6;
  s.rotation.y = Math.PI / 2;
  const hub = new THREE.Mesh(sharedGeo('byj-hub', () => new THREE.CylinderGeometry(3.6, 3.6, 3.6, 24)), mat('#f2f1ec', { roughness: 0.5 }));
  hub.position.y = H + 8.4;
  const armMat = mat('#e0342a', { roughness: 0.45 });
  const arm = new THREE.Mesh(sharedGeo('byj-arm', () => {
    const sh = new THREE.Shape();
    sh.moveTo(2.4, -1.3);
    sh.lineTo(13, -1.3);
    sh.lineTo(13, -2.6);
    sh.lineTo(17.5, 0);
    sh.lineTo(13, 2.6);
    sh.lineTo(13, 1.3);
    sh.lineTo(2.4, 1.3);
    sh.closePath();
    return prism(sh, 1.6, 4);
  }), armMat);
  arm.position.y = H + 8.6;
  for (const m of [s, hub, arm]) {
    m.castShadow = true;
    shaft.add(m);
  }
  b.root.add(shaft);

  let target = 0;
  let shown = 0;
  const apply = () => {
    shaft.rotation.y = (-shown * Math.PI) / 180;
  };
  return {
    root: b.root,
    update(v) {
      target = v.angle || 0;
      (v.coils || []).forEach((on, i) => leds[i].set(on ? 1 : 0));
    },
    animate(dt) {
      const d = target - shown;
      if (Math.abs(d) < 0.01) {
        shown = target;
        apply();
        return false;
      }
      shown += d * Math.min(1, dt * 30);
      apply();
      return true;
    },
    reset() {
      leds.forEach((l) => l.set(0));
    },
  };
}

// NEMA 17 con driver A4988 ------------------------------------------------------------------------------
function chamferSquare(sz, c) {
  const h = sz / 2;
  const s = new THREE.Shape();
  s.moveTo(-h + c, -h);
  s.lineTo(h - c, -h);
  s.lineTo(h, -h + c);
  s.lineTo(h, h - c);
  s.lineTo(h - c, h);
  s.lineTo(-h + c, h);
  s.lineTo(-h, h - c);
  s.lineTo(-h, -h + c);
  s.closePath();
  return s;
}

const MICRO_DIP = { 1: [0, 0, 0], 2: [1, 0, 0], 4: [0, 1, 0], 8: [1, 1, 0], 16: [1, 1, 1] };

export function buildNema17(def, props) {
  const { S, H, MX, CW, CD, CX, PCB_Y, TOP, HDR_X, TERM_X } = NEMA;
  const b = new Builder();
  // Placa portadora (azul) con el A4988 enchufado
  const holes = [[CX - 12, -15, 1.4], [CX + 12, -15, 1.4], [CX - 12, 15, 1.4], [CX + 12, 15, 1.4]];
  const dipZ = -14.2;
  pcb(b.root, {
    key: 'a4988-carrier', w: CW, d: CD, y0: PCB_Y, color: '#1b4c9c', edge: '#143a78', holes: holes.map(([x, z, r]) => [x - CX, z, r]),
    draw: (g, X, Y) => {
      traces(g, 'rgba(10,30,80,0.6)', [
        ...[0, 1, 2, 3, 4].map((i) => [[X(HDR_X - CX), Y((i - 2) * P)], [X(-8.5), Y((i - 2) * P)]]),
        [[X(TERM_X - CX), Y(14)], [X(-6.35), Y(8.9)]],
        [[X(8.5), Y(-3.8)], [X(11), Y(-3.8)]],
        [[X(8.5), Y(3.8)], [X(11), Y(3.8)]],
      ], 0.6);
      ['EN', 'STEP', 'DIR', 'VDD', 'GND'].forEach((n, i) => {
        pad(g, X(HDR_X - CX), Y((i - 2) * P), 0.95, i === 0);
        silk(g, n, X(HDR_X - CX + 2.0), Y((i - 2) * P), { size: 0.85, align: 'left' });
      });
      silk(g, 'VMOT', X(TERM_X - CX), Y(9.9), { size: 0.9 });
      silk(g, 'GND', X(TERM_X - CX + 5), Y(9.9), { size: 0.9 });
      silk(g, 'MS1 MS2 MS3', X(0), Y(-10.6), { size: 0.8 });
      silk(g, 'A4988', X(9.5), Y(-11.5), { size: 0.9 });
      silk(g, '2B 2A 1A 1B', X(12.3), Y(0), { size: 0.75, rotate: -Math.PI / 2 });
    },
  }).position.x = CX;
  holeRings(b, holes, TOP);
  maleHeader(b, [0, 1, 2, 3, 4].map((i) => [HDR_X, (i - 2) * P]), TOP);
  screwTerminal(b, TERM_X + 2.5, CD / 2 - 3.8, 2, TOP, '+z', '#2b8a4b');
  electrolytic(b.root, CX + 7.5, TOP, 13.4, 3.15, 7.7, { color: '#14161c' });
  // Zócalo hembra y módulo A4988 (verde) encima
  const female = M.blackPlastic;
  for (const s of [-1, 1]) {
    b.box(2.54, 8.5, 20.32, female, CX + s * 6.35, TOP, 0);
    for (let i = 0; i < 8; i++) b.box(1.0, 0.05, 1.0, M.hole, CX + s * 6.35, TOP + 8.5, (i - 3.5) * P);
  }
  const modY = TOP + 8.5;
  pcb(b.root, {
    key: 'a4988-module', w: 15.24, d: 20.32, y0: modY, color: '#1e6b3a', edge: '#144d2a', r: 0.5, s: 20,
    draw: (g, X, Y) => {
      const left = ['EN', 'MS1', 'MS2', 'MS3', 'RST', 'SLP', 'STP', 'DIR'];
      const right = ['VMT', 'GND', '2B', '2A', '1A', '1B', 'VDD', 'GND'];
      for (let i = 0; i < 8; i++) {
        const z = (i - 3.5) * P;
        pad(g, X(-6.35), Y(z), 0.75);
        pad(g, X(6.35), Y(z), 0.75);
        silk(g, left[i], X(-5.1), Y(z), { size: 0.62, align: 'left' });
        silk(g, right[i], X(5.1), Y(z), { size: 0.62, align: 'right' });
      }
      silk(g, 'A4988', X(0), Y(9.2), { size: 0.8 });
    },
  }).position.x = CX;
  const modTop = modY + 1.6;
  for (const s of [-1, 1]) for (let i = 0; i < 8; i++) b.box(0.64, 1.0, 0.64, M.tin, CX + s * 6.35, modTop, (i - 3.5) * P);
  b.box(5, 0.9, 5, M.chip, CX, modTop, -2.4);
  const alu = mat('#c8ccd2', { roughness: 0.35, metalness: 0.85 });
  b.box(9, 1.4, 9, alu, CX, modTop + 0.9, -2.4);
  for (let i = 0; i < 5; i++) b.box(9, 3.6, 0.9, alu, CX, modTop + 2.3, -2.4 - 3.6 + i * 1.8);
  b.box(3.6, 1.6, 3.6, mat('#e9e6dc', { roughness: 0.5 }), CX - 2.6, modTop, 6.4);
  b.cyl(1.4, 1.4, 0.4, M.steel, CX - 2.6, modTop + 1.6, 6.4, 14);
  b.box(2.6, 0.12, 0.45, M.hole, CX - 2.6, modTop + 2.0, 6.4);
  for (const z of [5.0, 8.0]) b.box(3.2, 0.55, 1.6, mat('#18181b', { roughness: 0.5 }), CX + 3.0, modTop, z);
  // Interruptores de micropasos (MS1–MS3)
  b.box(9.6, 3.6, 6.6, mat('#c8282a', { roughness: 0.45 }), CX, TOP, dipZ);
  for (let i = 0; i < 3; i++) b.box(1.5, 0.3, 4.2, M.hole, CX + (i - 1) * P, TOP + 3.45, dipZ);
  // Conector del motor y cable de 4 hilos
  const white = mat('#eeeae1', { roughness: 0.55 });
  b.box(5.8, 6.2, 10.4, white, CX + CW / 2 - 3.2, TOP, 0);
  b.box(4.4, 3.6, 9.6, mat('#f6f3eb', { roughness: 0.5 }), CX + CW / 2 - 3.2, TOP + 6.2, 0);
  ['#1e1f22', '#2f9e4a', '#d23a2e', '#2f6fe0'].forEach((c, i) => {
    const z = (i - 1.5) * 2.5;
    const zm = (i - 1.5) * 2.0;
    cable(b, [[CX + CW / 2 - 3.2, TOP + 9.6, z], [CX + CW / 2 - 1, TOP + 11.5, z], [MX - S / 2 - 3.5, 8, zm], [MX - S / 2 - 1.6, 4.6, zm]], 0.5, c, 20);
  });

  // Motor: tapas negras, estator laminado, centrador, agujeros y etiqueta
  const black = mat('#1c1e22', { roughness: 0.42, metalness: 0.45 });
  const capGeo = sharedGeo('nema-cap', () => prism(chamferSquare(S, 4.2), 8.5, 1));
  b.add(capGeo, black, { x: MX, y: 0 });
  b.add(capGeo, black, { x: MX, y: H - 8.5 });
  b.add(sharedGeo('nema-core', () => prism(chamferSquare(S - 0.6, 4.4), H - 17, 1)), mat('#4a4f56', { roughness: 0.5, metalness: 0.7 }), { x: MX, y: 8.5 });
  const lam = sharedGeo('nema-lam', () => prism(chamferSquare(S - 0.2, 4.25), 0.5, 1));
  const lamMat = mat('#9aa0a8', { roughness: 0.35, metalness: 0.85 });
  for (let y = 8.9; y < H - 8.9; y += 0.92) b.add(lam, lamMat, { x: MX, y });
  b.cyl(11, 11, 2, mat('#c9cdd3', { roughness: 0.25, metalness: 0.9 }), MX, H, 0, 40);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.cyl(1.6, 1.6, 0.05, M.hole, MX + sx * 15.5, H, sz * 15.5, 14);
  b.box(2.6, 5.2, 12.4, white, MX - S / 2 - 1.2, 1.8, 0);
  b.build();

  const label = labelPlane(30, 15, 'nema-label', (g, w, d) => {
    g.fillStyle = '#e9ebee';
    g.fillRect(0, 0, w, d);
    g.fillStyle = '#c9ccd1';
    g.fillRect(0, 0, w, 3.2);
    text(g, 'STEPPING MOTOR', w / 2, 1.7, { size: 1.6, color: '#2b2e33', weight: 800 });
    text(g, '17HS4401', w / 2, 6.2, { size: 3.0, color: '#15171a', weight: 800 });
    text(g, '1.8°/STEP   1.7A   1.5Ω', w / 2, 9.8, { size: 1.6, color: '#2b2e33', weight: 600 });
    text(g, 'NEMA 17   200 STEPS/REV', w / 2, 12.6, { size: 1.4, color: '#2b2e33', weight: 600 });
  }, { transparent: false });
  label.position.set(MX, H / 2, S / 2 + 0.02);
  b.root.add(label);

  // Palancas de los interruptores: ON hacia atrás (−z)
  const levers = [0, 1, 2].map((i) => {
    const m = new THREE.Mesh(sharedGeo('dip-lever', () => new THREE.BoxGeometry(1.3, 0.9, 1.8)), mat('#f2f1ec', { roughness: 0.5 }));
    m.position.set(CX + (i - 1) * P, TOP + 3.6, dipZ);
    b.root.add(m);
    return m;
  });
  const dipLabel = labelPlane(9.6, 1.6, 'dip-on', (g, w, d) => {
    text(g, 'ON', 1.4, d / 2, { size: 1.1, color: '#f5f5f0', weight: 800 });
    text(g, '1   2   3', w / 2 + 1.2, d / 2, { size: 1.0, color: '#f5f5f0', weight: 700 });
  });
  dipLabel.rotation.x = -Math.PI / 2;
  dipLabel.position.set(CX, TOP + 3.62, dipZ - 2.55);
  b.root.add(dipLabel);
  const setMicro = (m) => {
    const bits = MICRO_DIP[Number(m)] || MICRO_DIP[1];
    levers.forEach((l, i) => {
      l.position.z = dipZ + (bits[i] ? -1.2 : 1.2);
    });
  };
  setMicro(props.micro);

  // Eje (con su plano) e indicador rojo
  const shaft = new THREE.Group();
  shaft.position.set(MX, 0, 0);
  const shaftMesh = new THREE.Mesh(sharedGeo('nema-shaft', () => {
    // Eje de 5 mm con un plano (forma de D)
    const s = new THREE.Shape();
    const a = Math.acos(2.0 / 2.5);
    s.moveTo(2.0, 1.5);
    s.absarc(0, 0, 2.5, a, TAU - a, false);
    s.lineTo(2.0, 1.5);
    return prism(s, 24, 20);
  }), M.steel);
  shaftMesh.position.y = H + 2;
  const red = mat('#d0352c', { roughness: 0.3, metalness: 0.6 });
  const hub = new THREE.Mesh(sharedGeo('nema-hub', () => new THREE.CylinderGeometry(5.5, 5.5, 7, 28)), red);
  hub.position.y = H + 18.5;
  const pointer = new THREE.Mesh(sharedGeo('nema-pointer', () => {
    const s = new THREE.Shape();
    s.moveTo(4, -2.2);
    s.lineTo(17, -2.2);
    s.lineTo(22, 0);
    s.lineTo(17, 2.2);
    s.lineTo(4, 2.2);
    s.closePath();
    return prism(s, 4, 4);
  }), red);
  pointer.position.y = H + 16.5;
  const screw = new THREE.Mesh(sharedGeo('nema-screw', () => new THREE.CylinderGeometry(1.1, 1.1, 0.6, 12).rotateZ(Math.PI / 2)), M.hole);
  screw.position.set(-5.5, H + 18.5, 0);
  for (const m of [shaftMesh, hub, pointer, screw]) {
    m.castShadow = true;
    shaft.add(m);
  }
  b.root.add(shaft);

  let target = 0;
  let shown = 0;
  const apply = () => {
    shaft.rotation.y = (-shown * Math.PI) / 180;
  };
  return {
    root: b.root,
    update(v) {
      target = v.angle || 0;
    },
    setProps(p) {
      setMicro(p.micro);
    },
    animate(dt) {
      const d = target - shown;
      if (Math.abs(d) < 0.01) {
        shown = target;
        apply();
        return false;
      }
      shown += d * Math.min(1, dt * 30);
      apply();
      return true;
    },
    reset() {},
  };
}

// Motorreductor JGA25-370 con encoder Hall ----------------------------------------------------------------
// El eje del motor va a lo largo de X (el de salida mira hacia +X). Origen: unión de la lata con la caja
// reductora, a la altura del eje; la mesa está en y = 0.
function magnetTexture() {
  // Anillo magnético del encoder: 22 polos (11 pares) alternados
  return sharedTexture('gm-magnet', 12, 12, (g) => {
    g.fillStyle = '#16181b';
    g.fillRect(0, 0, 12, 12);
    for (let i = 0; i < 22; i++) {
      g.fillStyle = i % 2 ? '#2b2e33' : '#7b8087';
      g.beginPath();
      g.moveTo(6, 6);
      g.arc(6, 6, 5.9, (i * TAU) / 22, ((i + 1) * TAU) / 22);
      g.fill();
    }
    g.fillStyle = '#c3c6cb';
    g.beginPath();
    g.arc(6, 6, 1.4, 0, TAU);
    g.fill();
  }, 24);
}

function markerTexture() {
  // Disco del eje de salida: dos cuadrantes negros y una marca roja para ver el sentido y la velocidad
  return sharedTexture('gm-disc', 30, 30, (g) => {
    g.fillStyle = '#f3f2ec';
    g.fillRect(0, 0, 30, 30);
    g.fillStyle = '#1d1e22';
    for (const q of [0, 2]) {
      g.beginPath();
      g.moveTo(15, 15);
      g.arc(15, 15, 15, (q * Math.PI) / 2, ((q + 1) * Math.PI) / 2);
      g.fill();
    }
    g.fillStyle = '#d8352a';
    g.beginPath();
    g.arc(15, 15, 12.5, -0.22, 0.22);
    g.arc(15, 15, 8, 0.22, -0.22, true);
    g.fill();
  }, 12);
}

export function buildGearMotor(def, props) {
  const { AXIS_Y: Y, R, CAN_LEN: CAN, SHAFT } = GEARMOTOR;
  const box = GEARMOTOR.BOX[props.ratio] || GEARMOTOR.BOX[21];
  const b = new Builder();
  const zinc = mat('#b4b9bf', { roughness: 0.38, metalness: 0.85 });
  const tin = mat('#9da3aa', { roughness: 0.35, metalness: 0.85 });
  const dark = mat('#2d3035', { roughness: 0.5, metalness: 0.4 });
  const sheet = mat('#7c828a', { roughness: 0.45, metalness: 0.8 });
  // Base y abrazadera (media caña con dos orejas) bajo la lata
  b.rbox(30, 1.4, 36, 0.6, sheet, -17, 0, 0);
  const strap = new THREE.MeshStandardMaterial({ color: '#7c828a', roughness: 0.45, metalness: 0.8, side: THREE.DoubleSide });
  b.add(sharedGeo('gm-strap', () => new THREE.CylinderGeometry(R + 1.2, R + 1.2, 9, 36, 1, true, Math.PI, Math.PI).rotateZ(Math.PI / 2)), strap, { x: -17, y: Y });
  for (const z of [-1, 1]) {
    b.box(9, 1.3, 5.5, sheet, -17, Y - 1.3, z * (R + 1.2 + 2.7));
    b.cyl(1.3, 1.3, 0.5, M.steel, -17, Y, z * (R + 1.2 + 3.2), 12);
  }
  // Lata del motor con su tapa trasera
  b.cylX(R, CAN, mat('#8b9199', { roughness: 0.5, metalness: 0.75 }), -CAN / 2, Y, 0, 40);
  b.cylX(R - 0.2, 2.2, M.blackPlastic, -CAN - 1.1, Y, 0, 40);
  // Caja reductora metálica con su placa frontal, los dos agujeros M3, el buje y el eje
  b.cylX(12.5, box, zinc, box / 2, Y, 0, 40);
  b.cylX(12.6, 1.4, tin, box - 0.7, Y, 0, 40);
  for (const z of [-8.5, 8.5]) b.cylX(1.25, 1.5, M.hole, box - 0.65, Y, z, 12);
  b.cylX(3.2, 1.8, tin, box + 0.9, Y, 0, 24);
  // Encoder: placa redonda con dos sensores Hall sobre separadores y el anillo magnético detrás
  const pcbX = -CAN - 4.2;
  for (const a of [0.5, 2.6, 4.7]) b.cylX(0.8, 1.4, tin, -CAN - 2.9, Y + 9 * Math.cos(a), 9 * Math.sin(a), 8);
  b.cylX(11.4, 1.2, mat('#17713f', { roughness: 0.5 }), pcbX, Y, 0, 40);
  for (const a of [0.78, 0.78 + 0.14]) b.box(1.3, 3, 2.6, M.chip, pcbX - 1.25 - 0.65, Y + 7.6 * Math.cos(a) - 1.5, 7.6 * Math.sin(a));
  b.cylX(1.0, 8.2, M.steel, pcbX - 2.1, Y, 0, 10);
  const ring = new THREE.Mesh(
    sharedGeo('gm-ring', () => new THREE.CylinderGeometry(5.6, 5.6, 2.6, 44).rotateZ(Math.PI / 2)),
    [mat('#20232a', { roughness: 0.5 }), new THREE.MeshStandardMaterial({ map: magnetTexture(), roughness: 0.5 }), mat('#20232a', { roughness: 0.5 })],
  );
  ring.position.set(pcbX - 3.7, Y, 0);
  ring.castShadow = true;
  b.root.add(ring);
  // Cable de seis hilos (M1, GND, A, B, VCC, M2) hasta el conector hembra de 1 × 6
  const wires = ['#d23a2e', '#1e1f22', '#e8c11d', '#2f9e4a', '#2f6fe0', '#f2f2ee'];
  const zOf = (i) => (i - 2.5) * P;
  wires.forEach((c, i) => {
    cable(b, [[pcbX + 0.4, Y - 9.5, (i - 2.5) * 0.9], [pcbX - 4, Y - 11, (i - 2.5) * 1.1], [-52, 6, zOf(i) * 0.5], [GEARMOTOR.LEAD_X + 15, 1.6, zOf(i) * 0.9], [GEARMOTOR.LEAD_X + 9, 1.3, zOf(i)]], 0.5, c, 30);
  });
  b.box(14.5, 2.6, 6 * P + 0.6, M.blackPlastic, GEARMOTOR.LEAD_X + 7.25, 0, 0);
  for (let i = 0; i < 6; i++) b.box(0.2, 1.2, 1.2, M.hole, GEARMOTOR.LEAD_X - 0.05, 0.7, zOf(i));
  // Soporte del freno: poste y brazo por encima del disco del eje
  const discX = box + SHAFT - 1.6;
  const discR = 14;
  const armY = Y + discR + 9;
  b.box(3, armY + 0.8, 3, dark, discX, 0, -17.5);
  b.box(3, 1.6, 17.5 + 1.5, dark, discX, armY - 0.8, -8.5);
  b.build();

  // Rotor de salida: eje en D, buje y disco marcador (giran con el eje)
  const rotor = new THREE.Group();
  rotor.position.set(box, Y, 0);
  const shaft = new THREE.Mesh(sharedGeo('gm-shaft', () => {
    const sh = new THREE.Shape();
    const a = Math.acos(1.5 / 2);
    sh.moveTo(1.5, Math.sin(a) * 2);
    sh.absarc(0, 0, 2, a, TAU - a, false);
    sh.closePath();
    const g = new THREE.ExtrudeGeometry(sh, { depth: SHAFT, bevelEnabled: false, curveSegments: 16 });
    g.rotateY(Math.PI / 2); // la extrusión (z) pasa a lo largo de X
    return g;
  }), M.steel);
  const hub = new THREE.Mesh(sharedGeo('gm-hub', () => new THREE.CylinderGeometry(4.2, 4.2, 5, 28).rotateZ(Math.PI / 2)), mat('#c9ccd1', { roughness: 0.28, metalness: 0.95 }));
  hub.position.x = SHAFT - 4.2;
  const rim = mat('#e7e5dd', { roughness: 0.5 });
  const disc = new THREE.Mesh(
    sharedGeo('gm-disc-geo', () => new THREE.CylinderGeometry(discR, discR, 1.4, 56).rotateZ(-Math.PI / 2)),
    [rim, new THREE.MeshStandardMaterial({ map: markerTexture(), roughness: 0.5 }), rim],
  );
  disc.position.x = SHAFT - 1.6;
  for (const m of [shaft, hub, disc]) {
    m.castShadow = true;
    rotor.add(m);
  }
  b.root.add(rotor);

  // Freno: zapata sobre el borde del disco que baja hasta apretarlo, colgada del brazo por un vástago
  const shoe = brakeShoe(5, 2.8, 8);
  const stem = new THREE.Mesh(sharedGeo('gm-stem', () => new THREE.BoxGeometry(1.8, 1, 2.6)), dark);
  b.root.add(shoe.mesh, stem);
  const setBrake = (f) =>
    shoe.set(f, (g) => {
      const y = Y + discR + 1.4 + 4 * (1 - g); // centro de la zapata: apoyada en el borde con el 100 %
      shoe.mesh.position.set(discX, y, 0);
      const top = y + 1.4;
      stem.scale.y = armY - 0.8 - top;
      stem.position.set(discX, top + stem.scale.y / 2, 0);
    });
  setBrake(props.brake);

  let rpm = 0;
  let shown = 0;
  let angle = 0;
  return {
    root: b.root,
    update(v) {
      rpm = v.rpm || 0;
    },
    setProps(p) {
      setBrake(p.brake);
    },
    animate(dt) {
      shown += (rpm - shown) * Math.min(1, dt * 10);
      if (Math.abs(shown) < 0.5 && rpm === 0) shown = 0;
      // Giro visible del eje de salida: se limita a 4 vueltas por segundo para que no parpadee
      const rps = Math.max(-4, Math.min(4, shown / 60));
      angle -= rps * TAU * dt; // positivo = horario visto desde el extremo del eje
      rotor.rotation.x = angle % TAU;
      return shown !== 0;
    },
    reset() {
      rpm = 0;
    },
  };
}
